import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';

import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import CarouselEditor from './CarouselEditor';
import ContainerEditorWrapper from './ContainerEditorWrapper';
// Import component editors
import HeroEditor from './HeroEditor';
import IframeEditor from './IframeEditor';
import ImageGalleryEditor from './ImageGalleryEditor';
import IngressEditor from './IngressEditor';
import SingleImageEditor from './SingleImageEditor';
import TextBoxEditor from './TextBoxEditor';
import TitleParagraphEditor from './TextEditorBase';
import {
  ComponentStyleProps,
  ImageObject,
  ContainerConfig,
} from '../../../pages/types';
import {
  validateWidth,
  validateHeight,
  validateSpacing,
  validateBorderRadius,
} from '../admin-utils';
import FileSelectorModal from '../FileSelectorModal';
import { ComponentProps } from '../../../App';
import ConfirmDialog from '../../confirm-dialog';

// Updated interface to only handle style properties
interface StyleOptionsProps {
  styles: ComponentStyleProps;
  handleStyleChange: (key: string, value: any) => void;
  open?: boolean;
  configParam: string;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
  onBackgroundImageSelect?: (fileUrl: string) => void;
}

const StyleOptions: React.FC<StyleOptionsProps> = ({
  styles,
  handleStyleChange,
  open = false,
  configParam,
  handleUpload,
  onBackgroundImageSelect,
}) => {
  const [showOptions, setShowOptions] = useState(open);
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false);

  const handleFileSelect = (fileUrl: string) => {
    // Convert string to ImageObject when selecting background image
    const imageObject = {
      url: fileUrl,
      title: 'Background Image',
      altText: 'Background image for this component',
    };
    handleStyleChange('backgroundImage', imageObject);
    setIsFileSelectorOpen(false);
    if (onBackgroundImageSelect) {
      onBackgroundImageSelect(fileUrl);
    }
  };

  // Helper function to get actual image URL from possibly string or ImageObject
  const getImageUrl = (image: string | ImageObject | undefined): string => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url;
  };

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="h6">Style Options</Typography>
      <IconButton onClick={() => setShowOptions(!showOptions)}>
        {showOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>

      {showOptions && (
        <Box display="flex" flexDirection="column" gap={2} mt={2} width="100%">
          {/* BACKGROUND SECTION */}
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Background
            </Typography>
            <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={2}>
              {/* Background Image Column */}
              <Box flex="0 0 auto">
                {styles.backgroundImage ? (
                  <>
                    <img
                      src={getImageUrl(styles.backgroundImage)}
                      alt={
                        typeof styles.backgroundImage === 'object'
                          ? styles.backgroundImage.altText
                          : 'background'
                      }
                      style={{
                        width: '150px',
                        height: 'auto',
                        marginBottom: '8px',
                      }}
                    />
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleStyleChange('backgroundImage', '')}
                        startIcon={<DeleteOutline />}
                      >
                        Remove Background Image
                      </Button>
                    </Box>

                    {/* Show title and alt text fields only if backgroundImage is an object */}
                    {typeof styles.backgroundImage === 'object' && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          label="Image Title"
                          value={
                            typeof styles.backgroundImage === 'object'
                              ? styles.backgroundImage.title || ''
                              : ''
                          }
                          onChange={(e) =>
                            handleStyleChange('backgroundImage', {
                              ...(typeof styles.backgroundImage === 'object'
                                ? styles.backgroundImage
                                : {
                                    url: styles.backgroundImage || '',
                                    title: '',
                                    altText: '',
                                  }),
                              title: e.target.value,
                            })
                          }
                          fullWidth
                          margin="dense"
                          size="small"
                        />
                        <TextField
                          label="Alt Text"
                          value={
                            typeof styles.backgroundImage === 'object'
                              ? styles.backgroundImage.altText || ''
                              : ''
                          }
                          onChange={(e) =>
                            handleStyleChange('backgroundImage', {
                              ...(typeof styles.backgroundImage === 'object'
                                ? styles.backgroundImage
                                : {
                                    url: styles.backgroundImage || '',
                                    title: '',
                                    altText: '',
                                  }),
                              altText: e.target.value,
                            })
                          }
                          fullWidth
                          margin="dense"
                          size="small"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setIsFileSelectorOpen(true)}
                    startIcon={<AddCircleOutline />}
                  >
                    Select Background Image
                  </Button>
                )}
              </Box>
              {/* Background Color Column */}
              <Box flex="1 0 250px">
                <TextField
                  label="Background Color"
                  value={styles.background || ''}
                  onChange={(e) =>
                    handleStyleChange('background', e.target.value)
                  }
                  margin="dense"
                  size="small"
                  fullWidth
                />
                <HexColorPicker
                  color={styles.background || '#000000'}
                  onChange={(color) => handleStyleChange('background', color)}
                  style={{ marginTop: '8px', width: '100%', maxWidth: '250px' }}
                />
              </Box>
            </Box>
          </Box>

          {/* TEXT SECTION */}
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Text
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2}>
              <Box flex="1 1 250px">
                <TextField
                  label="Font Color"
                  value={styles.color || ''}
                  onChange={(e) => handleStyleChange('color', e.target.value)}
                  fullWidth
                  margin="dense"
                  size="small"
                />
                <HexColorPicker
                  color={styles.color || '#000000'}
                  onChange={(color) => handleStyleChange('color', color)}
                  style={{ marginTop: '8px', width: '100%', maxWidth: '250px' }}
                />
              </Box>
              <Box flex="1 1 250px">
                <Typography variant="body2" mb={1}>
                  Text Alignment
                </Typography>
                <Select
                  value={styles.textAlign || 'left'}
                  onChange={(e) =>
                    handleStyleChange('textAlign', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>

          {/* LAYOUT SECTION */}
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Layout
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2}>
              <Box flex="1 1 200px">
                <TextField
                  label="Width"
                  value={styles.width || ''}
                  onChange={(e) => handleStyleChange('width', e.target.value)}
                  onBlur={(e) => {
                    const validValue = validateWidth(e.target.value);
                    handleStyleChange('width', validValue);
                  }}
                  fullWidth
                  margin="dense"
                  size="small"
                />
                <TextField
                  label="Height"
                  value={styles.height || ''}
                  onChange={(e) => handleStyleChange('height', e.target.value)}
                  onBlur={(e) => {
                    const validValue = validateHeight(e.target.value);
                    handleStyleChange('height', validValue);
                  }}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Box>
              <Box flex="1 1 200px">
                <TextField
                  label="Padding"
                  value={styles.padding || ''}
                  onChange={(e) => handleStyleChange('padding', e.target.value)}
                  onBlur={(e) => {
                    const validValue = validateSpacing(e.target.value);
                    handleStyleChange('padding', validValue);
                  }}
                  fullWidth
                  margin="dense"
                  size="small"
                />
                <TextField
                  label="Margin"
                  value={styles.margin || ''}
                  onChange={(e) => handleStyleChange('margin', e.target.value)}
                  onBlur={(e) => {
                    const validValue = validateSpacing(e.target.value);
                    handleStyleChange('margin', validValue);
                  }}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Box>
              <Box flex="1 1 200px">
                <TextField
                  label="Border Radius"
                  value={styles.borderRadius || ''}
                  onChange={(e) =>
                    handleStyleChange('borderRadius', e.target.value)
                  }
                  onBlur={(e) => {
                    const validValue = validateBorderRadius(e.target.value);
                    handleStyleChange('borderRadius', validValue);
                  }}
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Box>
              <Box flex="1 1 200px">
                <Typography variant="body2">Item Alignment</Typography>
                <Select
                  value={styles.alignItems || 'left'}
                  onChange={(e) =>
                    handleStyleChange('alignItems', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                >
                  <MenuItem value="flex-start">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="flex-end">Right</MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>

          {/* ADVANCED JSON STYLE EDITOR */}
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              mt: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Advanced Style Editor
            </Typography>

            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Edit styles directly as JSON. Be careful as this can override
                all other style settings.
              </Typography>
            </Box>

            <TextField
              label="Styles JSON"
              multiline
              fullWidth
              rows={8}
              defaultValue={JSON.stringify(styles, null, 2)}
              InputProps={{
                style: { fontFamily: 'monospace', fontSize: '0.9rem' },
              }}
              sx={{ mb: 1 }}
              onChange={(e) => {
                try {
                  // Try parsing to validate JSON but don't apply yet
                  JSON.parse(e.target.value);
                  e.target.setCustomValidity('');
                } catch (err) {
                  e.target.setCustomValidity('Invalid JSON');
                }
              }}
              onBlur={(e) => {
                try {
                  const newStyles = JSON.parse(e.target.value);
                  // Cannot use onChange here as it's not available in this component
                  // Instead, we'll need to update each property individually

                  // First, clear any existing styles not in the new object
                  Object.keys(styles).forEach((key) => {
                    if (newStyles[key] === undefined) {
                      handleStyleChange(key, '');
                    }
                  });

                  // Then apply all new style properties
                  Object.entries(newStyles).forEach(([key, value]) => {
                    handleStyleChange(key, value);
                  });
                } catch (err) {
                  // If JSON is invalid, don't apply changes
                  console.error('Invalid JSON', err);
                  // Reset to current valid JSON
                  e.target.value = JSON.stringify(styles, null, 2);
                }
              }}
            />

            <Typography variant="caption" color="text.secondary">
              Changes will be applied when you click outside the text area.
              Invalid JSON will be reverted.
            </Typography>
          </Box>
        </Box>
      )}

      {/* File selector modal for background image */}
      <FileSelectorModal
        open={isFileSelectorOpen}
        onClose={() => setIsFileSelectorOpen(false)}
        onSelect={handleFileSelect}
        configParam={configParam}
        handleUpload={handleUpload}
      />
    </>
  );
};

const ComponentEditor: React.FC<{
  type: string;
  props: ComponentProps;
  styles: ComponentStyleProps;
  onChange: (
    updatedProps: ComponentProps,
    updatedStyles: ComponentStyleProps,
  ) => void;
  onDelete?: () => void;
  configParam: string;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
}> = ({
  type,
  props,
  styles,
  onChange,
  onDelete: handleDelete,
  configParam,
  handleUpload,
}) => {
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );
  const [isSingleImageSelection, setIsSingleImageSelection] = useState(false);
  const [isGalleryMultipleSelection, setIsGalleryMultipleSelection] =
    useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(
    null,
  );
  const [imageToDeleteIndex, setImageToDeleteIndex] = useState<number | null>(
    null,
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handlePropChange = (key: string, value: any) => {
    const updatedProps = { ...props };
    if (value === '' || value === undefined) {
      delete updatedProps[key];
    } else {
      updatedProps[key] = value;
    }
    onChange(updatedProps, styles);
  };

  const handleStyleChange = (key: string, value: any) => {
    const updatedStyles = { ...styles };
    if (value === '' || value === undefined) {
      delete updatedStyles[key];
    } else {
      updatedStyles[key] = value;
    }
    onChange(props, updatedStyles);
  };

  // File selection handlers
  const handleFileSelect = (fileUrl: string) => {
    if (isSingleImageSelection) {
      // Handle SingleImage component image selection
      const newImageObject: ImageObject = {
        url: fileUrl,
        title: 'Image',
        altText: 'Image description',
      };
      handlePropChange('image', newImageObject);
      setIsSingleImageSelection(false);
    } else if (selectedImageIndex !== null) {
      // Create new image object
      const isNewImage = selectedImageIndex === -1;

      const updatedImages = [...(props.images || [])];
      let newImageObject: ImageObject;

      if (isNewImage) {
        // Create completely new object
        const imageTitle = `Image ${updatedImages.length + 1}`;
        newImageObject = {
          url: fileUrl,
          title: imageTitle,
          altText: `${imageTitle} description`,
        };
        // Add new image to the BEGINNING of the list
        updatedImages.unshift(newImageObject);
      } else {
        // Replace the existing image
        const existingImage = updatedImages[selectedImageIndex];

        // Preserve existing metadata if possible when replacing
        let title = `Image ${selectedImageIndex + 1}`;
        let altText = `Image ${selectedImageIndex + 1} description`;

        if (typeof existingImage === 'object') {
          title = existingImage.title || title;
          altText = existingImage.altText || altText;
        }

        newImageObject = {
          url: fileUrl,
          title,
          altText,
        };
        updatedImages[selectedImageIndex] = newImageObject;
      }

      handlePropChange('images', updatedImages);
      setSelectedImageIndex(null);
    } else {
      // For background image, handled in Style Options
      handleStyleChange('backgroundImage', {
        url: fileUrl,
        title: 'Background Image',
        altText: 'Background image for this component',
      });
    }
    setIsFileSelectorOpen(false);
  };

  // Handle multiple image selection for galleries
  const handleMultipleFileSelect = (fileUrls: string[]) => {
    const currentImages = [...(props.images || [])];
    const newImages = fileUrls.map((url, index) => ({
      url: url,
      title: `Image ${currentImages.length + index + 1}`,
      altText: `Image ${currentImages.length + index + 1} description`,
    }));

    // Prepend new images
    const updatedImages = [...newImages, ...currentImages];
    handlePropChange('images', updatedImages);
    setIsFileSelectorOpen(false);
  };

  // Image handlers
  const openImageSelector = (index: number) => {
    setSelectedImageIndex(index);
    setIsGalleryMultipleSelection(false);
    setIsFileSelectorOpen(true);
  };

  // Multiple image selector for galleries
  const openImageSelectorMultiple = () => {
    setSelectedImageIndex(null);
    setIsGalleryMultipleSelection(true);
    setIsFileSelectorOpen(true);
  };

  const handleRemoveImage = (index: number) => {
    setImageToDeleteIndex(index);
    setConfirmDialogOpen(true);
  };

  const confirmRemoveImage = () => {
    if (imageToDeleteIndex !== null) {
      const updatedImages = [...(props.images || [])];
      updatedImages.splice(imageToDeleteIndex, 1);
      handlePropChange('images', updatedImages);
      setImageToDeleteIndex(null);
    }
    setConfirmDialogOpen(false);
  };

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    const updatedImages = [...(props.images || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < updatedImages.length) {
      // Swap the images
      [updatedImages[index], updatedImages[targetIndex]] = [
        updatedImages[targetIndex],
        updatedImages[index],
      ];
      handlePropChange('images', updatedImages);
    }
  };

  const handleMoveImageToPosition = (
    currentIndex: number,
    newPosition: number,
  ) => {
    const updatedImages = [...(props.images || [])];
    // Convert to 0-based index
    const targetIndex = newPosition - 1;

    // Validate position is within bounds
    if (targetIndex < 0 || targetIndex >= updatedImages.length) {
      return; // Invalid position
    }

    // Don't do anything if position is the same
    if (currentIndex === targetIndex) {
      return;
    }

    // Remove the image from current position
    const [movedImage] = updatedImages.splice(currentIndex, 1);
    // Insert it at the new position
    updatedImages.splice(targetIndex, 0, movedImage);

    handlePropChange('images', updatedImages);
  };

  const handleUpdateImageMetadata = (
    index: number,
    field: 'title' | 'altText',
    value: string,
  ) => {
    const updatedImages = [...(props.images || [])];
    const image = updatedImages[index];

    // If the image is a string, convert it to an object first
    if (typeof image === 'string') {
      updatedImages[index] = {
        url: image,
        title: field === 'title' ? value : '',
        altText: field === 'altText' ? value : '',
      };
    } else {
      // If it's already an object, update the specific field
      updatedImages[index] = {
        ...image,
        [field]: value,
      };
    }

    handlePropChange('images', updatedImages);
  };

  // Handler for SingleImage selection
  const openSingleImageSelector = () => {
    setIsSingleImageSelection(true);
    setIsFileSelectorOpen(true);
  };

  // Render appropriate editor component based on type
  let contentEditor;
  switch (type) {
    case 'Container':
      const containerConfig: ContainerConfig = props.container || { items: [] };
      contentEditor = (
        <ContainerEditorWrapper
          container={containerConfig}
          onPropChange={handlePropChange}
          configParam={configParam}
          handleUpload={handleUpload}
        />
      );
      break;

    case 'Hero':
    case 'PageHeader':
      contentEditor = (
        <HeroEditor
          title={props.title}
          subtitle={props.subtitle}
          onPropChange={handlePropChange}
        />
      );
      break;

    case 'SingleImage':
      contentEditor = (
        <SingleImageEditor
          image={props.image}
          imageWidth={props.imageWidth}
          imageHeight={props.imageHeight}
          showCaption={props.showCaption}
          onPropChange={handlePropChange}
          openFileSelector={openSingleImageSelector}
        />
      );
      break;

    case 'Carousel':
      contentEditor = (
        <CarouselEditor
          images={props.images}
          carouselHeight={props.carouselHeight}
          onPropChange={handlePropChange}
          openImageSelector={openImageSelector}
          editImageIndex={editingImageIndex}
          setEditImageIndex={setEditingImageIndex}
          handleRemoveImage={handleRemoveImage}
          handleUpdateImageMetadata={handleUpdateImageMetadata}
        />
      );
      break;

    case 'ImageGallery':
      contentEditor = (
        <ImageGalleryEditor
          images={props.images}
          rowHeight={props.rowHeight}
          columns={props.columns}
          minColumns={props.minColumns}
          gap={props.gap}
          onPropChange={handlePropChange}
          openImageSelector={openImageSelector}
          openImageSelectorMultiple={openImageSelectorMultiple}
          editImageIndex={editingImageIndex}
          setEditImageIndex={setEditingImageIndex}
          handleRemoveImage={handleRemoveImage}
          handleUpdateImageMetadata={handleUpdateImageMetadata}
          handleMoveImage={handleMoveImage}
          handleMoveToPosition={handleMoveImageToPosition}
        />
      );
      break;

    case 'Iframe':
      contentEditor = (
        <IframeEditor
          iframeUrl={props.iframeUrl}
          iframeHeight={props.iframeHeight}
          iframeWidth={props.iframeWidth}
          onPropChange={handlePropChange}
        />
      );
      break;

    case 'Ingress':
      contentEditor = (
        <IngressEditor
          title={props.title}
          paragraph={props.paragraph}
          lineColor={props.lineColor}
          onPropChange={handlePropChange}
        />
      );
      break;

    case 'TitleParagraph':
      contentEditor = (
        <TitleParagraphEditor
          title={props.title}
          paragraph={props.paragraph}
          onPropChange={handlePropChange}
        />
      );
      break;

    case 'TextBox':
      contentEditor = (
        <TextBoxEditor
          texts={props.texts}
          textBoxGap={props.textBoxGap}
          onPropChange={handlePropChange}
        />
      );
      break;

    default:
      contentEditor = (
        <Typography>No specific options for this component type</Typography>
      );
  }

  return (
    <Box>
      {/* Content options specific to each component type */}
      {contentEditor}

      {/* File selector modal for all components that need it */}
      <FileSelectorModal
        open={isFileSelectorOpen}
        onClose={() => {
          setIsFileSelectorOpen(false);
          setIsGalleryMultipleSelection(false);
        }}
        onSelect={handleFileSelect}
        onSelectMultiple={handleMultipleFileSelect}
        allowMultipleSelection={isGalleryMultipleSelection}
        configParam={configParam}
        handleUpload={handleUpload}
      />

      {/* Style options shared across all components */}
      <StyleOptions
        styles={styles}
        handleStyleChange={handleStyleChange}
        configParam={configParam}
        handleUpload={handleUpload}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmRemoveImage}
        title="Delete Image?"
        content="Are you sure you want to delete this image? This action cannot be undone."
      />
    </Box>
  );
};

export default ComponentEditor;
